import { useEffect, useState, useCallback, Fragment } from 'react';
import { api } from '../../api';

const SEVERITY_OPTIONS = ['ERROR', 'CRITICAL', 'WARNING'];
const WINDOW_OPTIONS = [
  { label: '15m', minutes: 15 },
  { label: '1h', minutes: 60 },
  { label: '6h', minutes: 360 },
  { label: '24h', minutes: 1440 },
];

function age(ts) {
  if (!ts) return '—';
  const ms = Date.now() - new Date(ts).getTime();
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m`;
  return `${Math.round(ms / 3_600_000)}h`;
}

function copyRowToClipboard(row) {
  const lines = [
    `timestamp: ${row.timestamp}`,
    `severity: ${row.severity}`,
    `endpoint: ${row.endpoint || '—'}`,
    `request_id: ${row.request_id || '—'}`,
    `case_id: ${row.case_id || '—'}`,
    `document_id: ${row.document_id || '—'}`,
    `logger: ${row.logger || '—'}`,
    '',
    'message:',
    row.message,
  ];
  if (row.traceback) {
    lines.push('', 'traceback:', row.traceback);
  }
  navigator.clipboard.writeText(lines.join('\n')).catch(() => {
    // Fallback: window.prompt so operators can still get the text
    window.prompt('Copy:', lines.join('\n'));
  });
}

function severityColor(sev) {
  if (sev === 'CRITICAL') return { bg: '#fee2e2', fg: '#7f1d1d' };
  if (sev === 'ERROR') return { bg: '#fee2e2', fg: '#991b1b' };
  if (sev === 'WARNING') return { bg: '#fef3c7', fg: '#92400e' };
  return { bg: '#e5e7eb', fg: '#374151' };
}

export default function Errors() {
  const [rows, setRows] = useState([]);
  const [severity, setSeverity] = useState('ERROR');
  const [minutes, setMinutes] = useState(15);
  const [caseFilter, setCaseFilter] = useState('');
  const [expanded, setExpanded] = useState(new Set());
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState(null);

  const fetchErrors = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('severity', severity);
      params.set('minutes', String(minutes));
      params.set('limit', '100');
      if (caseFilter.trim()) params.set('case_id', caseFilter.trim());
      const d = await api.get(`/ops_admin/errors?${params.toString()}`);
      setRows(d.items || []);
      setMeta({
        project_id: d.project_id,
        filter: d.filter,
        generated_at: d.generated_at,
        count: d.count,
      });
    } catch (e) {
      setError(e.message || 'fetch failed');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [severity, minutes, caseFilter]);

  useEffect(() => {
    fetchErrors();
    const t = setInterval(fetchErrors, 30000);
    return () => clearInterval(t);
  }, [fetchErrors]);

  const toggleExpand = (key) => {
    setExpanded(prev => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  const pivotToRequestId = (request_id) => {
    if (!request_id) return;
    // Re-query scoped to this request_id by stuffing it into the case
    // filter — Cloud Logging filter generation handles structured
    // search on jsonPayload.case_id today. For request_id pivoting we
    // emit a one-off filter via the same endpoint — kept simple:
    // operators copy the request_id from the row and paste into a
    // gcloud query if they need a true full-thread view. (A native
    // pivot is a follow-up; this stays read-only and fast.)
    window.prompt(
      'request_id (copy for cross-reference):',
      request_id,
    );
  };

  return (
    <div className="ops-errors">
      <h2>Errors</h2>
      <p style={{ color: '#666', marginTop: -4 }}>
        Recent backend log entries from Cloud Logging. Auto-refreshes every 30s.
      </p>

      <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <strong>Severity:</strong>{' '}
          <select value={severity} onChange={e => setSeverity(e.target.value)}>
            {SEVERITY_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <strong>Window:</strong>{' '}
          <select value={minutes} onChange={e => setMinutes(Number(e.target.value))}>
            {WINDOW_OPTIONS.map(w => (
              <option key={w.minutes} value={w.minutes}>{w.label}</option>
            ))}
          </select>
        </div>
        <div>
          <input
            type="text"
            placeholder="case_id (exact match)"
            value={caseFilter}
            onChange={e => setCaseFilter(e.target.value)}
            style={{ padding: '4px 8px', width: 280, fontFamily: 'monospace', fontSize: 12 }}
          />
        </div>
        <button onClick={fetchErrors} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
        {meta && (
          <span style={{ color: '#666', fontSize: 12 }}>
            {meta.count} entries · project {meta.project_id}
          </span>
        )}
      </div>

      {error && (
        <div style={{ background: '#fee', padding: 8, marginBottom: 12, color: '#900' }}>
          {error}
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
            <th style={{ padding: 6, width: 90 }}>When</th>
            <th style={{ padding: 6, width: 90 }}>Severity</th>
            <th style={{ padding: 6 }}>Endpoint</th>
            <th style={{ padding: 6 }}>Message</th>
            <th style={{ padding: 6, width: 80 }}></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && !loading && (
            <tr><td colSpan={5} style={{ padding: 12, color: '#666' }}>No entries in this window.</td></tr>
          )}
          {rows.map((r, idx) => {
            const key = r.insert_id || `${r.timestamp}-${idx}`;
            const sevStyle = severityColor(r.severity);
            const isExpanded = expanded.has(key);
            return (
              <Fragment key={key}>
                <tr style={{ borderTop: '1px solid #eee' }}>
                  <td style={{ padding: 6, whiteSpace: 'nowrap' }}>{age(r.timestamp)} ago</td>
                  <td style={{ padding: 6 }}>
                    <span style={{
                      padding: '2px 6px', borderRadius: 4, fontWeight: 600,
                      background: sevStyle.bg, color: sevStyle.fg,
                    }}>
                      {r.severity}
                    </span>
                  </td>
                  <td style={{ padding: 6, fontFamily: 'monospace', fontSize: 11, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.endpoint}>
                    {r.endpoint || '—'}
                  </td>
                  <td style={{ padding: 6, maxWidth: 480, fontFamily: 'monospace', fontSize: 11 }}>
                    <button
                      onClick={() => toggleExpand(key)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left', color: '#0066cc' }}
                    >
                      {isExpanded ? '▾ ' : '▸ '}
                      <span style={{ color: '#000' }}>
                        {r.message.length > 200 ? r.message.slice(0, 200) + '…' : r.message}
                      </span>
                    </button>
                  </td>
                  <td style={{ padding: 6, whiteSpace: 'nowrap' }}>
                    <button onClick={() => copyRowToClipboard(r)} title="Copy full traceback + context">
                      Copy
                    </button>
                  </td>
                </tr>
                {isExpanded && (
                  <tr style={{ background: '#fafafa' }}>
                    <td colSpan={5} style={{ padding: '8px 12px' }}>
                      <div style={{ display: 'flex', gap: 16, marginBottom: 8, flexWrap: 'wrap', fontSize: 11 }}>
                        {r.request_id && (
                          <span>
                            <strong>request_id:</strong>{' '}
                            <button
                              onClick={() => pivotToRequestId(r.request_id)}
                              style={{ background: 'none', border: 'none', padding: 0, color: '#0066cc', cursor: 'pointer', fontFamily: 'monospace' }}
                            >
                              {r.request_id}
                            </button>
                          </span>
                        )}
                        {r.case_id && <span><strong>case_id:</strong> <code>{r.case_id}</code></span>}
                        {r.document_id && <span><strong>document_id:</strong> <code>{r.document_id}</code></span>}
                        {r.logger && <span><strong>logger:</strong> {r.logger}</span>}
                        <span><strong>timestamp:</strong> {r.timestamp}</span>
                      </div>
                      <div style={{ background: '#fff', border: '1px solid #ddd', padding: 8, marginBottom: 8 }}>
                        <strong style={{ display: 'block', marginBottom: 4 }}>Message</strong>
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 11 }}>{r.message}</pre>
                      </div>
                      {r.traceback && (
                        <div style={{ background: '#fff', border: '1px solid #ddd', padding: 8 }}>
                          <strong style={{ display: 'block', marginBottom: 4 }}>Traceback</strong>
                          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 11 }}>{r.traceback}</pre>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>

      {meta && (
        <details style={{ marginTop: 16, color: '#666', fontSize: 11 }}>
          <summary>Filter sent to Cloud Logging</summary>
          <pre style={{ background: '#f5f5f5', padding: 8, fontFamily: 'monospace' }}>{meta.filter}</pre>
        </details>
      )}
    </div>
  );
}
